import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Building2, CalendarDays, MessageSquare } from 'lucide-react';
import { getAppOriginColor, getAppOriginLabel } from '../../services/appOriginService';
import { getStatusClass, getStatusLabel } from '../../services/statusService';
import { getPriorityLabel } from '../../services/priorityService';
import { getDemandTypeLabel } from '../../services/demandTypeService';
import { formatResponsibleLabel, getTicketResponsibleUsers } from '../../utils/ticketDisplayUtils';
import TicketProtocolLabel from '../shared/TicketProtocolLabel';

const PRIORITY_KEYS = ['critical', 'high', 'medium', 'low'];

const CommentsButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  margin-right: -4px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--color-hover-overlay);
    color: var(--color-text-primary);
  }
`;

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 0.875rem 0.875rem 0.625rem;
  background: var(--color-bg-primary);
  border: 1px solid
    ${({ $selected }) => ($selected ? 'var(--color-text-primary)' : 'var(--color-border-primary)')};
  border-radius: 10px;
  color: inherit;
  text-decoration: none;
  cursor: ${({ $draggable }) => ($draggable ? 'grab' : 'pointer')};
  touch-action: ${({ $draggable }) => ($draggable ? 'none' : 'auto')};
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease,
    opacity 0.15s ease;

  &:visited {
    color: inherit;
  }

  &:hover {
    border-color: var(--color-border-secondary);
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
  }

  &:active {
    cursor: ${({ $draggable }) => ($draggable ? 'grabbing' : 'pointer')};
  }

  &:focus-visible {
    outline: 2px solid var(--color-text-primary);
    outline-offset: 2px;
  }

  ${({ $isDragging }) =>
    $isDragging &&
    css`
      opacity: 0.4;
      transform: rotate(1deg);
    `}

  ${({ $isUpdating }) =>
    $isUpdating &&
    css`
      opacity: 0.6;
      pointer-events: none;
    `}

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
`;

const Context = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-secondary);

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const OriginDot = styled.i`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $color }) => $color || 'var(--color-border-accent)'};
`;

const ContextSeparator = styled.span`
  color: var(--color-border-accent);
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
`;

const PriorityPill = styled(Pill)`
  background: ${({ $priority }) => `var(--badge-priority-${$priority}-bg)`};
  color: ${({ $priority }) => `var(--badge-priority-${$priority}-fg)`};
  -webkit-text-fill-color: ${({ $priority }) => `var(--badge-priority-${$priority}-fg)`};
`;

const StatusPill = styled(Pill)`
  align-self: flex-start;
  background: ${({ $status }) => `var(--badge-status-${$status}-bg)`};
  color: ${({ $status }) => `var(--badge-status-${$status}-fg)`};
  -webkit-text-fill-color: ${({ $status }) => `var(--badge-status-${$status}-fg)`};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: -0.005em;
  color: var(--color-text-primary);
  display: -webkit-box;
  -webkit-line-clamp: ${({ $lines }) => $lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid var(--color-border-primary);
  font-size: 0.75rem;
  color: var(--color-text-muted);
`;

const Avatars = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const Avatar = styled.span`
  width: 22px;
  height: 22px;
  margin-left: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  border: 2px solid var(--color-bg-primary);
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  font-size: 0.625rem;
  font-weight: 600;

  &:first-child {
    margin-left: 0;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const FooterMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const FooterSpacer = styled.span`
  flex: 1;
  min-width: 0;
`;

const formatShortDate = (dateString) => {
  if (!dateString) return '';
  try {
    return format(parseISO(dateString), "dd MMM", { locale: ptBR });
  } catch {
    return '';
  }
};

const formatFullDate = (dateString) => {
  if (!dateString) return '';
  try {
    return format(parseISO(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return '';
  }
};

const TicketCard = ({
  ticket,
  to,
  linkState,
  users,
  showStatus = false,
  titleLines = 2,
  selected = false,
  draggable = false,
  isDragging = false,
  isUpdating = false,
  onDragStart,
  onDragEnd,
  onClick,
  onOpenComments,
}) => {
  const priorityKey = String(ticket.priority || 'MEDIUM').toLowerCase();
  const priority = PRIORITY_KEYS.includes(priorityKey) ? priorityKey : 'medium';
  const originColor = getAppOriginColor(ticket.appOrigin);
  const originLabel = ticket.appOrigin ? getAppOriginLabel(ticket.appOrigin) : '';
  const demandLabel = ticket.demandType ? getDemandTypeLabel(ticket.demandType) : '';
  const responsibleUsers = getTicketResponsibleUsers(ticket, users);
  const responsibleLabel = formatResponsibleLabel(ticket, users);
  const subject = ticket.subjectDescription || ticket.subject || 'Sem assunto';
  const createdAt = ticket.creationDate || ticket.createdAt;

  return (
    <Card
      to={to}
      state={linkState}
      draggable={draggable && !isUpdating}
      aria-busy={isUpdating || undefined}
      $draggable={draggable}
      $selected={selected}
      $isDragging={isDragging}
      $isUpdating={isUpdating}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      <TopRow>
        <Context>
          <OriginDot $color={originColor} aria-hidden />
          {originLabel ? <span>{originLabel}</span> : null}
          {originLabel && demandLabel ? <ContextSeparator aria-hidden>·</ContextSeparator> : null}
          {demandLabel ? <span>{demandLabel}</span> : null}
        </Context>
        <PriorityPill $priority={priority}>{getPriorityLabel(ticket.priority)}</PriorityPill>
      </TopRow>

      <Body>
        <Title title={subject} $lines={titleLines}>
          {subject}
        </Title>
        <TicketProtocolLabel ticket={ticket} compact />
      </Body>

      {showStatus && ticket.status ? (
        <StatusPill $status={getStatusClass(ticket.status)}>{getStatusLabel(ticket.status)}</StatusPill>
      ) : null}

      <Footer>
        {responsibleUsers.length > 0 ? (
          <Avatars title={responsibleLabel || undefined}>
            {responsibleUsers.slice(0, 3).map((user) => (
              <Avatar key={user.id || user.email} title={user.name}>
                {user.photoUrl ? (
                  <img src={user.photoUrl} alt="" />
                ) : (
                  (user.name || '?').charAt(0).toUpperCase()
                )}
              </Avatar>
            ))}
            {responsibleUsers.length > 3 ? <Avatar>+{responsibleUsers.length - 3}</Avatar> : null}
          </Avatars>
        ) : null}
        {ticket.companyName ? (
          <FooterMeta title={ticket.companyName}>
            <Building2 size={12} strokeWidth={2} aria-hidden />
            <span>{ticket.companyName}</span>
          </FooterMeta>
        ) : null}
        <FooterSpacer />
        {createdAt ? (
          <FooterMeta as="time" dateTime={createdAt} title={formatFullDate(createdAt)}>
            <CalendarDays size={12} strokeWidth={2} aria-hidden />
            {formatShortDate(createdAt)}
          </FooterMeta>
        ) : null}
        {onOpenComments ? (
          <CommentsButton
            type="button"
            title="Ver comentários"
            aria-label="Ver comentários"
            draggable={false}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenComments();
            }}
          >
            <MessageSquare size={14} strokeWidth={2} aria-hidden />
          </CommentsButton>
        ) : null}
      </Footer>
    </Card>
  );
};

export default TicketCard;
